import * as React from 'react';

import {
  ApolloLink,
  createHttpLink,
  from,
  HttpLink,
  HttpOptions,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import {
  InMemoryCache as NextSSRInMemoryCache,
  SSRMultipartLink,
} from '@apollo/experimental-nextjs-app-support';
import { fragments } from '@storefront/data-commerce';
import { getTraceInfo } from '@storefront/logging';
import { env } from 'next-runtime-env';

import { buildPath } from '@storefront/utils/utils';
import { errorLink } from '../../graphql/apolloErrorLink';
import { fetchWithTimeout } from '../fetch';
import { getSSRMode, isRSC } from '../getSSRMode';
import { magentoTypePolicie } from '../magentoTypePolicie';
import { mergeTypePolicies } from '../typePolicy';
import { AuthProvider } from '@storefront/clients/subscriptionStateService/subscriptionStateService';

export const ssrHttpLink = (override?: HttpOptions) =>
  createHttpLink({
    uri: env('ADOBE_COMMERCE_GRAPHQL_URL'),
    credentials: 'same-origin',
    fetch: (uri: any, options: any): Promise<Response> => {
      const FIFTY_SECONDS_IN_MS = 50000;
      return fetchWithTimeout(uri, FIFTY_SECONDS_IN_MS, options);
    },
    ...override,
  });

export const clientHttpLink = (override?: HttpOptions) =>
  new HttpLink({
    uri: buildPath('/api/graphql'),
    credentials: 'same-origin',
    fetchOptions: {
      credentials: 'same-origin',
    },
    ...override,
    // Additional fetch() options like `credentials` or `headers`
  });

export const getApolloLink = (
  ssrLinks: (HttpLink | SSRMultipartLink)[],
  appendLinks: ApolloLink[]
) =>
  from([
    requestIdLink,
    ...appendLinks,
    errorLink,
    ...(getSSRMode() ? ssrLinks : [clientHttpLink()]),
  ]);

// Middleware link to add x-request-id header
export const requestIdLink = new ApolloLink((operation, forward) => {
  const requestId = getTraceInfo().trace_id;
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-request-id': requestId,
    },
  }));
  return forward(operation);
});

export const authLink: (
  authProvider: AuthProvider
) => ApolloLink | undefined = (authProvider: AuthProvider) =>
  setContext(async (_, { headers }) => {
    // get the authentication token from local storage if it exists
    const access_token = await authProvider.getAccessToken();
    if (access_token) {
      const newHeaders = { ...headers };
      if (getSSRMode()) {
        newHeaders['Authorization'] = `Bearer ${access_token.replace(
          'Bearer ',
          ''
        )}`;
      }
      return {
        headers: newHeaders,
      };
    }

    return undefined;
  });

export function createCache() {
  const cacheClass = isRSC(React) ? NextSSRInMemoryCache : InMemoryCache;
  return new cacheClass({
    possibleTypes: fragments.possibleTypes,
    typePolicies: mergeTypePolicies([magentoTypePolicie]),
  });
}
